import React, { useEffect, useRef, useState } from 'react';
import { RiSearchLine, RiStarFill } from '@remixicon/react';
import { Card, CardContent } from '@components/ui/Card';
import { Input } from '@components/ui/Input';
import { TrustScore } from '@/features/trust-score';
import { apiClient } from '@/lib/api';
import Modal from '@components/ui/Modal';

/**
 * SelectOperatorModal — lightweight single-select operator picker. Extracted
 * from the operator tab of SearchAircraft so the Add Aircraft Manually
 * flow can capture a real `operator_id` without touching the full multi-tab
 * search UI. Same debounced /operators lookup + favorites strip, but each
 * card is a one-click select-and-close instead of a multi-select toggle.
 */
const SelectOperatorModal = ({ isOpen, onClose, onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(true);

  // Drag-to-scroll for the horizontal card strips (parity with SearchAircraft).
  const searchScrollRef = useRef(null);
  const favoriteScrollRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const handleMouseDown = (e, scrollRef) => {
    isDragging.current = true;
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeft.current = scrollRef.current.scrollLeft;
    scrollRef.current.style.cursor = 'grabbing';
    scrollRef.current.style.userSelect = 'none';
  };
  const handleMouseLeave = (scrollRef) => {
    isDragging.current = false;
    if (scrollRef.current) {
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = '';
    }
  };
  const handleMouseUp = (scrollRef) => {
    isDragging.current = false;
    if (scrollRef.current) {
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = '';
    }
  };
  const handleMouseMove = (e, scrollRef) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    scrollRef.current.scrollLeft = scrollLeft.current - walk;
  };

  // Reset state when the modal reopens so it always starts fresh.
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setResults([]);
  }, [isOpen]);

  // Fetch favorite operators once per open — same top-5 rating-tier order
  // used by SearchAircraft.
  const favoritesFetchedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) return;
    if (favoritesFetchedRef.current) return;
    favoritesFetchedRef.current = true;
    const fetchFavorites = async () => {
      setFavoritesLoading(true);
      try {
        const operators = await apiClient.getOperators(0, 5, {
          sort_by: 'rating_tiers',
          sort_order: 'desc',
        });
        setFavorites(operators);
      } catch (err) {
        console.error('Error fetching favorite operators:', err);
      } finally {
        setFavoritesLoading(false);
      }
    };
    fetchFavorites();
  }, [isOpen]);

  // Debounced operator search — 300ms, same window as SearchAircraft.
  useEffect(() => {
    if (!isOpen) return;
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const operators = await apiClient.getOperators(0, 10, { name: query.trim() });
        setResults(operators);
      } catch (err) {
        console.error('Error searching operators:', err);
        setResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, isOpen]);

  const handlePick = (operator) => {
    onSelect?.(operator);
    onClose?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title="Select Operator"
      headerClassName="!border-0"
      closeOnOutsideClick={false}
    >
      <Card className="overflow-hidden !border-0">
        <CardContent className="px-4 pt-0 pb-5">
          <p className="text-sm text-gray-500 mb-4">
            Search for an operator to assign to this aircraft.
          </p>

          <div className="space-y-4">
            <div>
              <p className="text-xs text-gray-500 mb-2">Operator Name</p>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <RiSearchLine className="h-4 w-4 text-gray-400" />
                </div>
                <Input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Enter operator name (e.g., Pinnacle Air Services)"
                  className="pl-9 h-11"
                  autoFocus
                />
              </div>
            </div>

            {query.trim() ? (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-3">Search Results</p>
                {searchLoading ? (
                  <div className="text-sm text-gray-500 py-4 text-center">Searching...</div>
                ) : results.length > 0 ? (
                  <div
                    ref={searchScrollRef}
                    className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] cursor-grab"
                    onMouseDown={(e) => handleMouseDown(e, searchScrollRef)}
                    onMouseLeave={() => handleMouseLeave(searchScrollRef)}
                    onMouseUp={() => handleMouseUp(searchScrollRef)}
                    onMouseMove={(e) => handleMouseMove(e, searchScrollRef)}
                  >
                    {results.map((operator) => (
                      <button
                        key={operator.operator_id}
                        onClick={() => handlePick(operator)}
                        className="grid flex-shrink-0 w-56 overflow-hidden border rounded-xl p-4 hover:shadow-sm transition-all text-left snap-start border-gray-200 bg-white hover:border-gray-300"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 text-sm break-words">{operator.name}</h4>
                            {(operator.faa_city || operator.faa_state) && (
                              <p className="text-xs text-gray-500 break-words">
                                {operator.faa_city}{operator.faa_state && `, ${operator.faa_state}`}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center">
                            <TrustScore score={operator.rating_tiers} numericScore={operator?.trust_score ?? null} size="sm" variant="bar" />
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {operator.argus_rating && operator.argus_rating !== 'No' && (
                            <span className="text-[10px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">{operator.argus_rating}</span>
                          )}
                          {operator.wyvern_rating && operator.wyvern_rating !== 'No' && (
                            <span className="text-[10px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">Wyvern</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 py-4 text-center">No operators found</div>
                )}
              </div>
            ) : (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-3">Favorite Operators</p>
                {favoritesLoading ? (
                  <div className="text-sm text-gray-500 py-4 text-center">Loading favorite operators...</div>
                ) : favorites.length > 0 ? (
                  <div
                    ref={favoriteScrollRef}
                    className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] cursor-grab"
                    onMouseDown={(e) => handleMouseDown(e, favoriteScrollRef)}
                    onMouseLeave={() => handleMouseLeave(favoriteScrollRef)}
                    onMouseUp={() => handleMouseUp(favoriteScrollRef)}
                    onMouseMove={(e) => handleMouseMove(e, favoriteScrollRef)}
                  >
                    {favorites.map((operator) => (
                      <button
                        key={operator.operator_id}
                        onClick={() => handlePick(operator)}
                        className="grid flex-shrink-0 w-56 overflow-hidden border rounded-xl p-4 hover:shadow-sm transition-all text-left snap-start items-start border-gray-200 bg-white hover:border-gray-300"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 text-sm break-words">{operator.name}</h4>
                            {(operator.faa_city || operator.faa_state) && (
                              <p className="text-xs text-gray-500 break-words">
                                {operator.faa_city}{operator.faa_state && `, ${operator.faa_state}`}
                              </p>
                            )}
                          </div>
                          <RiStarFill className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center">
                            <TrustScore score={operator?.rating_tiers} numericScore={operator?.trust_score ?? null} size="sm" variant="bar" showBadge />
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {operator.argus_rating && operator.argus_rating !== 'No' && (
                            <span className="text-[10px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">ARGUS {operator.argus_rating}</span>
                          )}
                          {operator.wyvern_rating && operator.wyvern_rating !== 'No' && (
                            <span className="text-[10px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">Wyvern</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 py-4 text-center">No favorite operators</div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Modal>
  );
};

export default SelectOperatorModal;
